import { PermissionMetadataItem } from "../../../api";
import { DataModel } from "../../../models/data-model";
import {
  addPermissionWithImplied,
  getPermissionState,
  isDerivedState,
  type PermissionRules,
  type PermissionState,
  removePermissionWithDependents,
} from "./permissions-utils";

/**
 * DataModel for the permissions picker. Overrides the base selection methods so
 * DataTable's built-in selection column automatically applies the implied /
 * forbidden rules: selecting a permission pulls in its implied bits and drops
 * conflicting ones, and deselecting removes any bits that depended on it.
 */
export class PermissionsModel extends DataModel<PermissionMetadataItem> {
  constructor(
    data: PermissionMetadataItem[],
    private rules: PermissionRules,
  ) {
    super(data);
    this.idAttr = "bit";
    this.selectionMode = "multi";
  }

  override select(id: number, _exclusive: boolean) {
    this.selected = addPermissionWithImplied(id, this.selected, this.rules);
    if (this.selectionMode) this.computeData();
  }

  override deselect(id: number) {
    this.selected = removePermissionWithDependents(
      id,
      this.selected,
      this.rules,
    );
    if (this.selectionMode) this.computeData();
  }

  override selectAllItems(items: PermissionMetadataItem[]) {
    let selection: number[] = [];
    for (const item of items) {
      selection = addPermissionWithImplied(item.bit, selection, this.rules);
    }
    this.selected = selection;
    if (this.selectionMode) this.computeData();
  }

  stateOf(bit: number): PermissionState {
    return getPermissionState(bit, new Set(this.selected), this.rules);
  }

  isSelectDisabled = (item: PermissionMetadataItem) => {
    return isDerivedState(this.stateOf(item.bit));
  };
}
